/**
 * Lectura y escritura de los aplicadores con carné (`company_applicators`, F3
 * 26-09-2026): las personas de la empresa con su carné y las empresas de
 * servicios.
 *
 * Sin react-query a propósito, como `equipmentApi`: la usan formularios que
 * cargan sus listas al abrirse con consultas sueltas.
 *
 * La RLS manda: leen el admin y el encargado (y cada persona su fila); escribe
 * solo el admin (D7, E6). Si alguien sin permiso llega a guardar, el error dice
 * POR QUÉ (E8).
 */
import { supabase } from "@/integrations/supabase/client";
import type { ApplicatorKind, ApplicatorRow, LicenseType } from "@/lib/applicators/applicatorRules";
import { licenseExpiresOn } from "@/lib/applicators/applicatorRules";

/* El cliente con el que se lee y escribe: el del navegador por defecto; la
   tarjeta del asistente (CU7) pasa el suyo, que en las pruebas es uno de mentira. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Db = any;

export interface CompanyApplicator extends ApplicatorRow {
  company_id: string;
  created_at?: string;
}

const COLUMNS =
  "id, company_id, user_id, kind, display_name, nif, ropo_number, license_type, license_issued_on, " +
  "license_expires_on, is_advisor, advisor_ropo, active, archived_at, created_at";

/** La clave de react-query de los aplicadores de una empresa (la invalida también la tarjeta del asistente). */
export const companyApplicatorsQueryKey = (companyId: string | undefined) => ["company-applicators", companyId] as const;

/** Los aplicadores de la empresa, sin los archivados. */
export async function fetchCompanyApplicators(companyId: string, db: Db = supabase): Promise<CompanyApplicator[]> {
  const { data, error } = await db
    .from("company_applicators")
    .select(COLUMNS)
    .eq("company_id", companyId)
    .is("archived_at", null)
    .order("display_name", { ascending: true });
  if (error) throw error;
  return (data ?? []) as unknown as CompanyApplicator[];
}

/** El carné vivo de UNA persona en la empresa (o null). */
export async function fetchMemberApplicator(companyId: string, userId: string, db: Db = supabase): Promise<CompanyApplicator | null> {
  const { data, error } = await db
    .from("company_applicators")
    .select(COLUMNS)
    .eq("company_id", companyId)
    .eq("user_id", userId)
    .is("archived_at", null)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as unknown as CompanyApplicator | null;
}

export interface ApplicatorDraft {
  kind: ApplicatorKind;
  user_id: string | null;
  display_name: string;
  nif: string;
  ropo_number: string;
  license_type: LicenseType | "";
  license_issued_on: string;
  license_expires_on: string;
  is_advisor: boolean;
  advisor_ropo: string;
}

export const EMPTY_APPLICATOR_DRAFT: ApplicatorDraft = {
  kind: "member",
  user_id: null,
  display_name: "",
  nif: "",
  ropo_number: "",
  license_type: "",
  license_issued_on: "",
  license_expires_on: "",
  is_advisor: false,
  advisor_ropo: "",
};

export function draftFromApplicator(a: CompanyApplicator | null, base: Partial<ApplicatorDraft> = {}): ApplicatorDraft {
  if (!a) return { ...EMPTY_APPLICATOR_DRAFT, ...base };
  return {
    kind: a.kind === "service_company" ? "service_company" : "member",
    user_id: a.user_id,
    display_name: a.display_name ?? "",
    nif: a.nif ?? "",
    ropo_number: a.ropo_number ?? "",
    license_type: (a.license_type as LicenseType) ?? "",
    license_issued_on: a.license_issued_on ?? "",
    license_expires_on: a.license_expires_on ?? "",
    is_advisor: !!a.is_advisor,
    advisor_ropo: a.advisor_ropo ?? "",
  };
}

const orNull = (s: string) => {
  const v = s.trim();
  return v ? v : null;
};

/**
 * Lo que falta para poder guardar, como clave i18n (o null si está bien). La
 * BD lo vuelve a exigir: caducidad obligatoria (o expedición, de la que sale),
 * y asesor solo con nº ROPO de asesor (D18).
 */
export function applicatorDraftProblem(d: ApplicatorDraft): string | null {
  if (!d.display_name.trim()) return "applicationContext.applicators.errors.nameRequired";
  if (!licenseExpiresOn(orNull(d.license_issued_on), orNull(d.license_expires_on))) {
    return "applicationContext.applicators.errors.expiryRequired";
  }
  if (d.license_issued_on && d.license_expires_on && d.license_expires_on < d.license_issued_on) {
    return "applicationContext.applicators.errors.expiryBeforeIssue";
  }
  if (d.is_advisor && !d.advisor_ropo.trim()) return "applicationContext.applicators.errors.advisorRopoRequired";
  return null;
}

/** Alta o edición. Devuelve la fila guardada. */
export async function saveApplicator(
  companyId: string,
  createdBy: string | null,
  draft: ApplicatorDraft,
  id?: string | null,
  db: Db = supabase,
): Promise<CompanyApplicator> {
  const row = {
    company_id: companyId,
    kind: draft.kind,
    user_id: draft.kind === "member" ? draft.user_id : null,
    display_name: draft.display_name.trim(),
    nif: orNull(draft.nif),
    ropo_number: orNull(draft.ropo_number),
    license_type: draft.license_type || null,
    license_issued_on: orNull(draft.license_issued_on),
    // Sin caducidad escrita: expedición + 10 años (lo mismo que hace el trigger).
    license_expires_on: licenseExpiresOn(orNull(draft.license_issued_on), orNull(draft.license_expires_on)) as string,
    is_advisor: draft.is_advisor,
    advisor_ropo: draft.is_advisor ? orNull(draft.advisor_ropo) : null,
  };
  const res = id
    ? await db.from("company_applicators").update(row).eq("id", id).select(COLUMNS).single()
    : await db
        .from("company_applicators")
        .insert({ ...row, created_by: createdBy })
        .select(COLUMNS)
        .single();
  if (res.error) throw res.error;
  return res.data as unknown as CompanyApplicator;
}

/** Archivar, no borrar: el 1.2 de un cuaderno pasado lo sigue necesitando. */
export async function archiveApplicator(id: string): Promise<void> {
  const { error } = await supabase
    .from("company_applicators")
    .update({ archived_at: new Date().toISOString(), active: false })
    .eq("id", id);
  if (error) throw error;
}

/** Clave i18n del motivo de un fallo al guardar (E8). */
export function applicatorErrorKey(error: unknown): { key: string; code: string } {
  const e = error as { code?: string; message?: string } | null;
  const code = e?.code ?? "";
  const message = e?.message ?? "";
  if (code === "42501" || /row-level security/i.test(message)) {
    return { key: "applicationContext.applicators.errors.onlyAdmin", code };
  }
  if (/APPLICATOR_USER_NOT_IN_COMPANY/.test(message)) {
    return { key: "applicationContext.applicators.errors.userOtherCompany", code };
  }
  if (code === "23505") return { key: "applicationContext.applicators.errors.duplicate", code };
  if (/advisor_ropo/.test(message)) return { key: "applicationContext.applicators.errors.advisorRopoRequired", code };
  if (code === "23502" || /license_expires_on|dates_check/.test(message)) {
    return { key: "applicationContext.applicators.errors.expiryRequired", code };
  }
  return { key: "applicationContext.applicators.errors.generic", code: code || "?" };
}
